import React, { useContext, useState, useEffect } from 'react';
import { CheckboxField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from './RPSOCRPaperlessPreferences.css';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';

const RPSOCRPaperlessConsentReview = ({
  paperlessDelivery,
  paperlessConsentValid
}) => {
  return (
    <>
      {paperlessDelivery ? (
        <div className="paperless-review-section">
          <div className="consent-label">Paperless Consent</div>
          <div className="consent-value">Yes</div>
        </div>
      ) : (
        <>
          {paperlessConsentValid ? (
            <div className="no-selection">
              Participant has chosen not to consent for Paperless Delivery.
            </div>
          ) : (
            <div className="no-selection">
              You have not made the choice yet.
            </div>
          )}
        </>
      )}
      <style jsx>{styles}</style>
    </>
  );
};

const shouldValidate = (editPaperless, paperlessConsentValid) =>
  editPaperless || paperlessConsentValid;

const RPSOCRPaperlessPreferences = ({
  editPaperless,
  setEditPaperless,
  next,
  paperlessConsentValid,
  paperlessDelivery
}) => {
  const {
    appState: {
      store: {
        loadedData: { paperlessEditMode }
      }
    }
  } = useContext(FlowContext);

  const [paperlessDeliveryConsent, setPaperlessDeliveryConsent] = useState(
    paperlessDelivery
  );

  useEffect(() => {
    setEditPaperless(paperlessEditMode);
  }, [paperlessEditMode]);

  const isValid = shouldValidate(editPaperless, paperlessConsentValid);

  const handleClick = async () => {
    if (editPaperless) {
      await next({
        section: 'ocrPaperlessConsent',
        paperlessConsent: paperlessDeliveryConsent
      })();
    } else {
      await next('unlockRPSPaperlessSection')();
    }
  };

  return (
    <div>
      <div className="section-header">
        <SectionHeading text="Paperless Preferences" />
      </div>
      <div className="section-content">
        {editPaperless ? (
          <>
            <div className="consent-label">
              Are you interested in receiving Statements, Confirmations,
              Prospectuses and Shareholder Reports, Tax Forms, and/or Client
              Communications electronically?
            </div>

            <CheckboxField
              className="electronicAccessDeliveryConsent"
              title={
                <div className="cell">
                  If the participant is interested, check this box and see
                  talking points below.
                </div>
              }
              defaultChecked={paperlessDeliveryConsent}
              value={paperlessDeliveryConsent}
              onChange={() =>
                setPaperlessDeliveryConsent(!paperlessDeliveryConsent)
              }
            />

            <p className="cell">
              If the participant does not want to participate, click done and
              continue to the next section.
            </p>

            <p>Talking points:</p>

            <p>
              Going paperless is easier than ever, and offers a host of
              benefits, which include:
            </p>

            <ul>
              <li>
                Waive the $20 annual account service fee for mutual fund
                accounts below the minimum balance
              </li>
              <li>Timely delivery of important documents</li>
              <li>Convenient access to your documents anytime, anywhere</li>
              <li>Strong security protocols to safeguard sensitive data</li>
            </ul>

            <p>If paperless delivery is requested:</p>

            <ul>
              <li>
                After your new account is set up, we will send you an email with
                a link to access your account online and instructions for how to
                consent to the paperless services.
              </li>
              <li>
                The direct URL to paperless services is{' '}
                <a
                  href="https://www.troweprice.com/paperless"
                  target="_blank"
                  rel="noopener noreferrer">
                  www.troweprice.com/paperless
                </a>
              </li>
              <li>
                You will receive an email alerting you when new documents are
                available to view, print, and/or download.
              </li>
              <li>
                You may need to access some documents securely by logging into
                your account online. Some documents may still be delivered in
                paper through the mail.
              </li>
              <li>
                These preferences will apply to all of your current and future
                T. Rowe Price mutual fund, brokerage, and/or college savings
                plan account(s), not just the account(s) you are opening today.
              </li>
              <li>
                <strong>
                  Keep in mind that you will not be registered for electronic
                  delivery until you go online and consent.
                </strong>
              </li>
            </ul>
          </>
        ) : (
          <RPSOCRPaperlessConsentReview
            paperlessDelivery={paperlessDelivery}
            paperlessConsentValid={paperlessConsentValid}
          />
        )}
      </div>
      <EditButton
        onClick={handleClick}
        currentlyEditing={editPaperless}
        disabled={!isValid}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

export default RPSOCRPaperlessPreferences;
